import {
  IApiService,
  IPortalConfig,
  IUserConfig,
  ILoan,
  IUser,
  IAuth,
  ILoanRequest,
  IArticle,
  ISubscription,
  ICashbackRequest,
} from './types';
import {
  PortalConfig,
  UserConfig,
  User,
  Loan,
  Authenticate,
  LoanRequest,
  Article,
  Subscription,
  CashbackRequest,
} from './endpoints';
import Handler from './handler/Handler';
import HeadersManager from './handler/managers/HeadersManager';
import Wrapper from './wrapper/Wrapper';
import TokenManager from './handler/managers/TokenManager';
import {AxiosError} from 'axios';
import PreferenceManager from '../PreferenceManager/PreferenceManager';

/**
 * CreditBookApiService class implements IApiService interface.
 */
class CreditBookApiService implements IApiService {
  readonly portalConfig: IPortalConfig;
  readonly userConfig: IUserConfig;
  readonly user: IUser;
  readonly auth: IAuth;
  readonly loan: ILoan;
  readonly loanRequest: ILoanRequest;
  readonly article: IArticle;
  readonly subscription: ISubscription;
  readonly cashbackRequest: ICashbackRequest;

  readonly tokenManager: TokenManager;
  readonly headersManager: HeadersManager;

  readonly handler: Handler;

  /**
   * Constructor
   */
  constructor(
    endpoints: {
      portalConfig: IPortalConfig;
      userConfig: IUserConfig;
      user: IUser;
      auth: IAuth;
      loan: ILoan;
      loanRequest: ILoanRequest;
      article: IArticle;
      subscription: ISubscription;
      cashbackRequest: ICashbackRequest;
    },
    managers: {tokenManager: TokenManager; headersManager: HeadersManager},
    handler: Handler,
  ) {
    const {
      portalConfig,
      userConfig,
      auth,
      user,
      loan,
      loanRequest,
      article,
      subscription,
      cashbackRequest,
    } = endpoints;
    this.portalConfig = portalConfig;
    this.userConfig = userConfig;
    this.auth = auth;
    this.user = user;
    this.loan = loan;
    this.loanRequest = loanRequest;
    this.article = article;
    this.subscription = subscription;
    this.cashbackRequest = cashbackRequest;

    const {tokenManager, headersManager} = managers;
    this.headersManager = headersManager;
    this.tokenManager = tokenManager;

    this.handler = handler;
  }

  /**
   * Set the language
   */
  setLanguage(language: 'fr' | 'en') {
    this.headersManager.setLanguage(language);
  }

  /**
   * Set token
   */
  setToken(token: string) {
    this.tokenManager.setToken(token);
  }

  /**
   * Clear token
   */
  clearToken() {
    this.tokenManager.clear();
  }

  /**
   * On error
   */
  setErrorHandler(callback: (error: AxiosError) => void) {
    this.handler.setErrorHandler(callback);
  }
}

/**
 * Creates the API service instance
 * @param url API base URL - without the version (e.g., '/api')
 */
function createApiService(url: string): CreditBookApiService {
  const tokenManager = new TokenManager();
  const currentLanguage = PreferenceManager.getPreference()?.language || 'en';
  const headersManager = new HeadersManager(tokenManager, currentLanguage);
  const handler = new Handler(headersManager);
  const service = new Wrapper(url, handler);
  const portalConfig = new PortalConfig(service);
  const userConfig = new UserConfig(service);
  const user = new User(service);
  const loan = new Loan(service);
  const loanRequest = new LoanRequest(service);
  const auth = new Authenticate(service);
  const article = new Article(service);
  const subscription = new Subscription(service);
  const cashbackRequest = new CashbackRequest(service);

  return new CreditBookApiService(
    {
      portalConfig,
      userConfig,
      user,
      auth,
      loan,
      loanRequest,
      subscription,
      cashbackRequest,
      article,
    },
    {tokenManager, headersManager},
    handler,
  );
}

const apiURL = process.env.REACT_APP_BASE_URL || '';
const CreditBookApi = createApiService(apiURL);

export default CreditBookApi;
